<template>
  <div class="group">
    <div
      @click="handleToggle"
      @keydown.enter="handleToggle"
      class="px-8 py-6 w-full text-left text-white text-sm md:text-xs uppercase font-bold outline-none focus:outline-none
        active:outline-none focus:bg-secondary-600 dark:focus:bg-white/10 transition-all flex flex-nowrap justify-between items-center cursor-pointer subSideMenuButton whitespace-nowrap select-none
        lg:pl-3 lg:pr-2 lg:py-2 lg:text-gray-600 dark:lg:text-white/60 lg:font-bold lg:hover:bg-gray-lightest lg:hover:bg-opacity-80 dark:lg:hover:bg-white/[.04] lg:focus:bg-gray-lightest lg:focus:bg-opacity-80 dark:lg:focus:bg-white/[.04] lg:border-white dark:lg:border-navy-900 lg:border-l-4 lg:hover:border-primary dark:lg:hover:border-primary-300/70 fg:focus:border-primary lg:focus:border-primary dark:lg:focus:border-primary-300/70
      "
      tabindex="0"
    >
      {{ module.label }}
      <button class="p-2" tabindex="-1">
        <div
          class="flex items-center"
          :class="
            expanded
              ? 'transition-all transform rotate-180'
              : 'transition-all transform rotate-0'
          "
        >
          <font-awesome-icon icon="fa-solid fa-chevron-down" size="lg" class="h-4 w-4" />
        </div>
      </button>
    </div>
    <hr
      class="transition-all border-secondary-700 border-2 lg:border-gray-100 dark:border-navy-500 transform -scale-x-100 origin-left"
      :class="expanded ? 'scale-x-100' : '-scale-x-100'"
    >
    <ul
      class="w-full pl-6 pt-2 lg:bg-transparent transition-all overflow-hidden"
      :class="expansionClasses"
    >
      <li v-for="option in module.subnav" :key="option.label" tabindex="-1" :ref="`nav-${option.label}`">
        <div v-if="option.subnav && checkSidemenuPermissions(option)">
          <OptionWithSubnav :option="option" :subsection="subsection" is-deep :ref="`expandable-${option.label}`" />
        </div>
        <div v-if="!option.subnav && checkSidemenuPermissions(option)" tabindex="-1">
          <router-link :to="urlLink(option.route)" tabindex="-1">
            <button
              @click="select()"
              class="subSideMenuButton px-8 py-6 w-full text-left text-md uppercase text-sm md:text-xs font-medium text-white outline-none focus:bg-secondary-600 focus:outline-none active:outline-none transition-all select-none"
              :class="subnavClass(option.route)"
            >
              {{ option.label }}
            </button>
          </router-link>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex"
import OptionWithSubnav from "./OptionWithSubnav.vue"

export default {
  name: "ModuleSubnav",
  components: {
    OptionWithSubnav
  },
  props: {
    module: {
      type: Object,
      required: true,
    },
    subsection: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      expanded: false,
      clickedOpen: false,
      hideSubnav: false
    }
  },
  computed: {
    ...mapState([
      "sideMenuVisible",
      "screen",
      "orientation",
      "availableModules"
    ]),
    ...mapGetters([
      "getIsTenantSuperuser",
      "getUserManagementAccess",
      "getPermissionSet",
      "getAvailableViews",
      "getAvailableFeatures",
      "getPOSSystem",
      "getSiteCommandControllerFeatures",
      "sectionMenuCollapsed"
    ]),
    path() {
      return this.$route.fullPath;
    },
    availableViews() {
      return this.getAvailableViews?.[this.$route.meta.module][this.option.feature]
    },
    availableFeatures() {
      return this.getAvailableFeatures?.[this.$route.meta.module]
    },
    isModuleSelected() {
      return this.path.startsWith(`/${this.subsection}/${this.module.route}/`)
    },
    supportsInterpolateSize() {
      return CSS.supports('interpolate-size', 'allow-keywords')
    },
    expansionClasses() {
      let classes
      // PREFERRED
      if (this.supportsInterpolateSize) {
        classes = "fadein transition-all duration-150 rounded-b [interpolate-size:allow-keywords] overflow-hidden "
        if (this.expanded) classes += "visible h-auto opacity-100 lg:pt-2 lg:pb-5 "
        else classes += "invisible h-0 opacity-0 lg:py-0 "
      }
      // FALLBACK
      else {
        classes = "fadein transition-all duration-100 rounded-b overflow-hidden "
        if (this.expanded) classes += "visible max-h-[1000px] opacity-100 lg:pt-2 lg:pb-5 "
        else classes += "invisible max-h-0 opacity-50 lg:py-0 "
      }
      return classes
    },
  },
  watch: {
    sideMenuVisible() {
      this.hideSubnav = true
      for (let i = 0; i < this.module.subnav.length; i++) {
        if (this.$route.path.includes(this.module.subnav[i].route)) {
          this.hideSubnav = false
        }
      }
      if (this.hideSubnav === true) {
        this.setExpanded(false)
      }
    },
  },
  mounted() {
    this.expandIfSelected()
  },
  methods: {
    ...mapMutations(["CLOSE_MOBILEMENU"]),
    select() {
      this.CLOSE_MOBILEMENU()
    },
    setExpanded(bool) {
      this.expanded = bool
    },
    handleToggle() {
      this.setExpanded(!this.expanded)
      this.clickedOpen = true
    },
    expandIfSelected() {
      if (this.isModuleSelected) {
        this.expanded = true
      }
    },
    urlLink(path) {
      return this.$route.path.includes(this.subsection)
        ? `/${this.subsection}/${this.module.route}/${path}`
        : path
    },
    checkSidemenuPermissions(option) {
      // console.log("checking permissions for ", option)
      // check if option is a module grouping
      if (option.module) {
        return this.availableModules.includes(option.module)
      }

      // need to check if feature is available at site for Reports and Configuration
      // let moduleFeatures = {
      //   // if option name is relevant to either module, only show if site has access to module
      //   site_track: ["asset", "assets", "inventory", "purchase_orders", "locations", "manufacturers", "vendors"],
      //   site_scan: ["chemical", "thresholds"]
      // }
      // if (this.subsection === "Configuration" || this.subsection === "Reports") {
      //   for (let module of Object.keys(moduleFeatures)) {
      //     if (moduleFeatures[module].includes(option.feature) && !this.availableModules.includes(module)) return false
      //   }
      // }
      // check Site Command version features
      if (this.subsection === "SiteCommand" ) {
        if (option.feature === "edl_cil") {
          if (!this.getSiteCommandControllerFeatures?.includes("EDL_CIL")) return false
        }
        if (option.feature === "assistance_tool") {
          if (!this.getSiteCommandControllerFeatures?.includes("Bumps_Mirror")
            && !this.getSiteCommandControllerFeatures?.includes("Bumps_Wrap_HB")
            && !this.getSiteCommandControllerFeatures?.includes("Control_Word")
            && !this.getSiteCommandControllerFeatures?.includes("Omni_Control")
            && !this.getSiteCommandControllerFeatures?.includes("Requeue_Control_Word")
            && !this.getSiteCommandControllerFeatures?.includes("Omni_Vac")
            && !this.getSiteCommandControllerFeatures?.includes("Airgate_Table")
            && !this.getSiteCommandControllerFeatures?.includes("Dry_Run")
            && !this.getSiteCommandControllerFeatures?.includes("Conveyor_Speed_Config")
            && !this.getSiteCommandControllerFeatures?.includes("PID_Attack_Module")
            && !this.getSiteCommandControllerFeatures?.includes("Blower_Flips")
          ) return false
        }
      }
      // show Washlink only is site has access to washlink
      if (option.feature === "washlink" && this.getPOSSystem !== "washlink") return false
      // always show if site includes relevant module AND user is superuser
      // if (this.getIsTenantSuperuser) return true
      // hide internal options from non-superusers
      if (option.internal) return false
      // show Users & Permissions + chem thresholds if user has user_management privileges
      if (this.getUserManagementAccess && (
        option.feature === "user_management" || option.feature === "thresholds"
      )) return true
      if (!option.feature && option.subnav) {
        // if option has subnav, check if any subnav options are available
        for (let subOption of option.subnav) {
          if (this.checkSidemenuPermissions(subOption)) return true
        }
        return false
      }
      // otherwise base on user permissions
      return this.availableFeatures[option?.feature]
    },
    checkOptionsSidemenuPermissions(option) {
      if (option.feature && option.view == null) {
        // if option has a feature, check if feature is available
        if (this.getIsTenantSuperuser) return true
        if (option.internal) return false
        return this.availableFeatures?.[option.feature]
      }
      if (!option.view) return true
      // check Site Command version features
      if (this.subsection === "SiteCommand") {
        if (option.view === "edl" || option.view === "cil") {
          if (!this.getSiteCommandControllerFeatures?.includes("EDL_CIL")) return false
        }
        if (option.route.split("/")[0] === "assistance_tool") {
          if (option.view === "mirror_bumps") {
            if (!this.getSiteCommandControllerFeatures?.includes("Bumps_Mirror")
              && !this.getSiteCommandControllerFeatures?.includes("Bumps_Wrap_HB")
            ) return false
          }
          if (option.view === "control_word") {
            if (!this.getSiteCommandControllerFeatures?.includes("Control_Word_C5")
              && !this.getSiteCommandControllerFeatures?.includes("Control_Word_C4")
              && !this.getSiteCommandControllerFeatures?.includes("Control_Word_Standard_Offering")
              && !this.getSiteCommandControllerFeatures?.includes("Requeue_Control_Word")
            ) return false
          }
          if (option.view === "omni_control") {
            if (!this.getSiteCommandControllerFeatures?.includes("Omni_Control")) return false
          }
          if (option.view === "omni_vac") {
            if (!this.getSiteCommandControllerFeatures?.includes("Omni_Vac")) return false
          }
          if (option.view === "airgate_tables") {
            if (!this.getSiteCommandControllerFeatures?.includes("Airgate_Table")) return false
          }
          if (option.view === "dry_run") {
            if (!this.getSiteCommandControllerFeatures?.includes("Dry_Run")) return false
          }
          if (option.view === "conveyor_speed_selector") {
            if (!this.getSiteCommandControllerFeatures?.includes("Conveyor_Speed_Config")) return false
          }
          if (option.view === "pid_attack_module") {
            if (!this.getSiteCommandControllerFeatures?.includes("PID_Attack_Module")) return false
          }
          if (option.view === "blower_flips") {
            if (!this.getSiteCommandControllerFeatures?.includes("Blower_Flips")) return false
          }
        }
      }
      if (this.getIsTenantSuperuser) return true
      if (option.internal) return false
      // show Users & Permissions + chem thresholds if user has user_management privileges
      if (this.getUserManagementAccess && (
        option.route === "Users" || option.route === "Permissions" || option.route === "Thresholds/chemical"
      )) return true
      if (option.washlink  && !this.getPOSSystem) return false
      return this.availableViews?.[option.view]
    }
  },
}
</script>

<style scoped>
.subSideMenuButton {
  min-height: 64px !important;
  line-height: 1.4;
}

</style>